import React from 'react';
import { connect } from 'react-redux';
import DatePicker from 'react-datepicker';
import Papa from 'papaparse';
import { getTenantState, getAdminState } from '../../store/selectors';
import {
  getListOfTenantNames,
  getNewUserReportPages,
  getNewUserReport,
} from '../../store/actions';
import { reportingCopy } from '../../copy/advisor-dashboard/advisor-dashboard-base.json';
import CSVDownloadButton from './reporting-components/CSVDownloadButton';
import MultipleChoiceMenu from '../utility/MultipleChoiceMenu';
import 'react-datepicker/dist/react-datepicker.css';

export class NewUserReport extends React.Component {
  constructor(props) {
    super(props);

    const date = new Date();
    date.setDate(date.getDate() - 30);

    this.state = {
      choices: [],
      selectedChoices: [this.props.tenant.name],
      startDate: date,
      startString: date.toLocaleDateString('en-US'),
      endDate: new Date(),
      endString: new Date().toLocaleDateString('en-US'),
    };
  }

  async componentDidMount() {
    this.setState({ choices: await this.getChoices() });
  }

  getCsvFilename = () => {
    const filename = 'NewUserReport_';

    const selectedTenantNames = this.state.selectedChoices.includes('View All')
      ? 'all_tenants'
      : this.state.selectedChoices.join('_');

    const startYear = this.state.startDate.getFullYear();
    const startMonth = `${this.state.startDate.getMonth() + 1}`.padStart(
      2,
      '0'
    );
    const startDay = `${this.state.startDate.getDate()}`.padStart(2, '0');

    const endYear = this.state.endDate.getFullYear();
    const endMonth = `${this.state.endDate.getMonth() + 1}`.padStart(2, '0');
    const endDay = `${this.state.endDate.getDate()}`.padStart(2, '0');

    const datestamps = `_${startYear}${startMonth}${startDay}-${endYear}${endMonth}${endDay}`;

    return filename + selectedTenantNames + datestamps;
  };

  getChoices = async () => {
    if (this.props.admin.permissions.editableRoles.includes('Super User')) {
      const result = await this.props.getListOfTenantNames(
        this.props.tenant.id
      );
      return result.tenantNames;
    }
    return [this.props.tenant.name];
  };

  setSelectedChoices = (selectedChoices) => {
    this.setState({ selectedChoices });
  };

  onStartDateChange = (date) => {
    const formattedStart = date ? date.toLocaleDateString('en-US') : '0/0/0000';
    this.setState({
      startDate: date,
      startString: formattedStart,
    });
  };

  onEndDateChange = (date) => {
    const formattedEnd = date ? date.toLocaleDateString('en-US') : '0/0/0000';
    this.setState({
      endDate: date,
      endString: formattedEnd,
    });
  };

  displayCustomInputStartDate = () => {
    return (
      <div className="new-user-report__date-picker">
        <div className="new-user-report__date-picker-header">
          {reportingCopy.newUserReport.startDateLabel}
        </div>
        <div className="new-user-report__date-picker-date">
          {this.state.startString}
        </div>
      </div>
    );
  };

  displayCustomInputEndDate = () => {
    return (
      <div className="new-user-report__date-picker">
        <div className="new-user-report__date-picker-header">
          {reportingCopy.newUserReport.endDateLabel}
        </div>
        <div className="new-user-report__date-picker-date">
          {this.state.endString}
        </div>
      </div>
    );
  };

  toSqlDate = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();

    return `${year}-${month}-${day}`;
  };

  getCsv = async (tenantId, selectedTenant, startDate, endDate) => {
    const pageCount = await this.props.getNewUserReportPages(
      tenantId,
      selectedTenant,
      startDate,
      endDate
    );

    const allNewUserRows = [];
    const promises = [];

    for (let page = 0; page < pageCount; page++) {
      promises.push(
        this.props.getNewUserReport(
          tenantId,
          selectedTenant,
          startDate,
          endDate,
          page + 1
        )
      );
    }

    const newUserRows = await Promise.all(promises);

    newUserRows.map((result) => allNewUserRows.push(...result));

    const csvData = Papa.unparse(allNewUserRows);
    return csvData;
  };

  render() {
    return (
      <div className="new-user-report">
        <div className="new-user-report__header">
          <div className="new-user-report__heading">
            {reportingCopy.newUserReport.heading}
          </div>
        </div>
        <div className="new-user-report__subheader">
          <div className="new-user-report__subheading">
            {reportingCopy.newUserReport.subheading}
          </div>
        </div>
        <div className="new-user-report__controls">
          <MultipleChoiceMenu
            choices={this.state.choices}
            startingSelectedChoices={[this.props.tenant.name]}
            multipleChoice={false}
            color={this.props.tenant.primary}
            headerText={reportingCopy.newUserReport.tenantMenuLabel}
            setSelectedChoices={this.setSelectedChoices}
          />
          <div className="new-user-report__date-picker-container">
            <DatePicker
              fixedHeight
              selected={this.state.startDate}
              onChange={this.onStartDateChange}
              customInput={this.displayCustomInputStartDate()}
              maxDate={new Date()}
            />
          </div>
          <div className="new-user-report__date-picker-container">
            <DatePicker
              fixedHeight
              selected={this.state.endDate}
              onChange={this.onEndDateChange}
              customInput={this.displayCustomInputEndDate()}
              maxDate={new Date()}
            />
          </div>
          <CSVDownloadButton
            csv={async () => {
              const data = await this.getCsv(
                this.props.tenant.id,
                this.state.selectedChoices[0],
                this.toSqlDate(this.state.startDate),
                this.toSqlDate(this.state.endDate)
              );
              return data;
            }}
            filename={this.getCsvFilename()}
            color={this.props.tenant.primary}
          />
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  const tenant = getTenantState(state);
  const admin = getAdminState(state);
  return { tenant, admin };
}

export default connect(mapStateToProps, {
  getListOfTenantNames,
  getNewUserReport,
  getNewUserReportPages,
})(NewUserReport);
